import {Children} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import Switch from 'flarum/common/components/Switch';
import withAttr from 'flarum/common/utils/withAttr';
import cleanFieldConfiguration from '../cleanFieldConfiguration';
import FormGroup from '../components/FormGroup';
import PermissionDropdown from '../components/PermissionDropdown';
import ValidationErrors from '../components/ValidationErrors';
import Sortable from '../components/Sortable';
import SortableHandle from '../components/SortableHandle';
import Uploader from '../utils/Uploader';
import {
    FieldTemplate,
    OptionDefinition,
    SubmissionFieldPayload,
    SubmissionPayload,
    SubmissionValidationErrors
} from '../models/JsonTypes';

const translationPrefix = 'kilowhat-formulaire.forum.template-editor.';

interface FieldOptions {
    config?: FieldTemplate
    submission?: SubmissionPayload
    uploader?: Uploader | null
    onchange?: () => void
    onduplicate?: () => void
    ondelete?: () => void
    disabled?: boolean
    validationErrors?: SubmissionValidationErrors
    depth?: number
    expertMode?: boolean
}

export default abstract class Field {
    config: FieldTemplate
    submission: SubmissionPayload
    uploader: Uploader | null
    onchange: () => void
    onduplicate: () => void
    ondelete: () => void
    disabled: boolean
    validationErrors: SubmissionValidationErrors
    depth: number
    expertMode: boolean

    constructor({
                    config = {} as FieldTemplate,
                    submission = {},
                    uploader = null,
                    onchange = () => {
                    },
                    onduplicate = () => {
                    },
                    ondelete = () => {
                    },
                    disabled = false,
                    validationErrors = {},
                    depth = 0,
                    expertMode = false,
                }: FieldOptions) {
        this.config = config;
        this.submission = submission;
        this.uploader = uploader;
        this.onchange = onchange;
        this.onduplicate = onduplicate;
        this.ondelete = ondelete;
        this.disabled = disabled;
        this.validationErrors = validationErrors;
        this.depth = depth;
        this.expertMode = expertMode;

        // To ensure they have a unique key
        // Only run the logic when a config is passed. Otherwise there could be a loop with cleanFieldConfiguration
        if (Object.keys(this.config).length) {
            cleanFieldConfiguration(this.config);
        }
    }

    getConfig(key: keyof FieldTemplate, defaultValue: any = '') {
        return this.config[key] || defaultValue;
    }

    uniqueFieldId() {
        return 'formulaireField' + this.getConfig('unique');
    }

    setConfig(key: keyof FieldTemplate) {
        return (value: any) => {
            if (value === null || value === false || value === '') {
                delete this.config[key];
            } else {
                this.config[key] = value;
            }

            if (key === 'type') {
                cleanFieldConfiguration(this.config);
            }

            this.onchange();
        };
    }

    getSubmission(defaultValue: any = '', property: keyof SubmissionFieldPayload = 'value') {
        const entry = this.submission[this.getConfig('key')];

        if (entry) {
            return entry[property];
        }

        return defaultValue;
    }

    setSubmission(value: any): void {
        if (value === null || value === false || value === '' || (Array.isArray(value) && value.length === 0)) {
            delete this.submission[this.getConfig('key')];
        } else {
            if (!this.submission[this.getConfig('key')]) {
                this.submission[this.getConfig('key')] = {
                    value: '', // Temporary value because of the type-hinting
                };
            }

            this.submission[this.getConfig('key')].value = value;
        }

        // Remove validation errors for this field when edited
        const theKeyInValidationErrorTree = this.validationErrors[this.getConfig('key')];
        if (theKeyInValidationErrorTree) {
            delete theKeyInValidationErrorTree.errors;
        }

        this.onchange();
    }

    whiteListConfigProperties(): (keyof FieldTemplate)[] {
        return [
            'title',
            'description',
            'required',
            'key',
            'fillGroupIds',
        ];
    }

    abstract configure(): Children;

    configureView(): Children {
        const validationErrorsForAllFieldsHere: string[] = [];

        Object.keys(this.validationErrors).forEach(key => {
            if (!this.validationErrors.hasOwnProperty(key)) {
                return;
            }

            // If there are errors for the template field itself, when we reach the key "errors" this test will be true
            if (Array.isArray(this.validationErrors[key])) {
                validationErrorsForAllFieldsHere.push(...this.validationErrors[key]);
            }

            // If there are errors for individual properties of the field, they will be collected here
            if (this.validationErrors[key].errors) {
                validationErrorsForAllFieldsHere.push(...this.validationErrors[key].errors);
            }

            // This loop will skip "items" field sub-errors, because it will be an object inside an object
            // Which shouldn't have any key named errors (unless a sub-field has key "errors" of course)
        });

        const whitelistedProperties = this.whiteListConfigProperties();

        const typeOptions: any = {
            short: app.translator.trans(translationPrefix + 'type.short'),
            long: app.translator.trans(translationPrefix + 'type.long'),
            number: app.translator.trans(translationPrefix + 'type.number'),
            date: app.translator.trans(translationPrefix + 'type.date'),
            checkbox: app.translator.trans(translationPrefix + 'type.checkbox'),
            radio: app.translator.trans(translationPrefix + 'type.radio'),
            select: app.translator.trans(translationPrefix + 'type.select'),
            content: app.translator.trans(translationPrefix + 'type.content'),
        };

        if (this.depth === 0) {
            typeOptions.upload = app.translator.trans(translationPrefix + 'type.upload');
            typeOptions.items = app.translator.trans(translationPrefix + 'type.items');
        }

        return m('.FormulaireConfiguration', {
            key: this.getConfig('unique'),
            className: validationErrorsForAllFieldsHere.length ? 'in-error' : '',
        }, [
            m(SortableHandle, {
                className: 'FormulaireConfiguration-handle',
                disabled: this.disabled,
            }),
            m(ValidationErrors, {
                errors: validationErrorsForAllFieldsHere,
            }),
            m('.Form-group', [
                whitelistedProperties.indexOf('title') === -1 ? null : m('.FormulaireInputWithLabel', {
                    className: this.getConfig('title') ? 'FormulaireInputWithLabel--with-value' : '',
                }, [
                    m('input.FormControl', {
                        type: 'text',
                        value: this.getConfig('title'),
                        onchange: withAttr('value', this.setConfig('title')),
                        disabled: this.disabled,
                    }),
                    m('label', app.translator.trans(translationPrefix + 'title')),
                ]),
                Select.component({
                    value: this.getConfig('type'),
                    options: typeOptions,
                    //TODO: shouldn't keep min/max, shouldn't keep key if there are existing submissions
                    onchange: this.setConfig('type'),
                    disabled: this.disabled,
                }),
            ]),
            whitelistedProperties.indexOf('description') === -1 ? null : m('.Form-group', m('.FormulaireInputWithLabel', {
                className: this.getConfig('description') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('textarea.FormControl', {
                    value: this.getConfig('description'),
                    onchange: withAttr('value', this.setConfig('description')),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'description')),
            ])),
            this.configure(),
            this.expertMode && whitelistedProperties.indexOf('key') !== -1 ? [
                m('.Form-group', m('.FormulaireInputWithLabel', {
                    className: this.getConfig('key') ? 'FormulaireInputWithLabel--with-value' : '',
                }, [
                    m('input.FormControl', {
                        type: 'text',
                        value: this.getConfig('key'),
                        onchange: withAttr('value', this.setConfig('key')),
                        disabled: this.disabled,
                    }),
                    m('label', app.translator.trans(translationPrefix + 'key')),
                ])),
                // Only show permissions for fields with a key. Otherwise it means it can't be filled anyway
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'fill-permission')),
                    PermissionDropdown.component({
                        groupIds: this.getConfig('fillGroupIds', null),
                        onchange: this.setConfig('fillGroupIds'),
                    }),
                ]),
            ] : null,
            m('.Form-group', [
                whitelistedProperties.indexOf('required') === -1 ? null : Switch.component({
                    state: this.getConfig('required', false),
                    onchange: this.setConfig('required'),
                    disabled: this.disabled,
                }, app.translator.trans(translationPrefix + 'required')),
                ' ',
                Button.component({
                    onclick: this.onduplicate.bind(this),
                    className: 'Button Button--icon',
                    icon: 'fas fa-clone',
                    disabled: this.disabled,
                    title: app.translator.trans(translationPrefix + 'duplicate'),
                }),
                ' ',
                Button.component({
                    onclick: this.ondelete.bind(this),
                    className: 'Button Button--icon',
                    icon: 'fas fa-trash',
                    disabled: this.disabled,
                    title: app.translator.trans(translationPrefix + 'delete'),
                }),
            ]),
        ]);
    }

    choiceView(renderLabelAndInput: (checked: boolean, onchange: () => void, disabled: boolean, key: string, children: Children) => Children, display: boolean, radio: boolean = false) {
        const options: OptionDefinition[] = this.getConfig('options', []);

        let values = this.getSubmission() as string[];

        if (!Array.isArray(values)) {
            values = [];
        }

        const allOptionKeys = options.map(option => option.key);

        const otherValue = values.find(value => allOptionKeys.indexOf(value) === -1);
        const hasOtherValue = typeof otherValue !== 'undefined';

        return m('.FormulaireChoice-OptionGroup', {
            className: radio ? 'FormulaireChoice--radio' : 'FormulaireChoice--checkbox',
        }, [
            options.map(option => {
                const checkedIndex = values.indexOf(option.key as string);
                const checked = checkedIndex !== -1;

                return m('.FormulaireChoice-Option', renderLabelAndInput(checked, () => {
                    if (checked) {
                        this.setSubmission(values.filter(value => value !== option.key));
                    } else if (radio) {
                        this.setSubmission([
                            option.key,
                        ]);
                    } else {
                        this.setSubmission([
                            // Add new key at the top so it's easier to keep the "other" value at the end
                            option.key,
                            ...values,
                        ]);
                    }
                }, this.isFillDisabled() || display, option.key as string, [
                    ' ',
                    option.title,
                ]));
            }),
            (this.getConfig('other') && (!display || hasOtherValue)) ? (() => {
                const otherOnChange = (value: string) => {
                    const valuesWithoutOther = values.filter(value => allOptionKeys.indexOf(value) !== -1);

                    this.setSubmission([
                        ...valuesWithoutOther,
                        value,
                    ]);
                };

                return m('.FormulaireChoice-Option.FormulaireChoice--other', [
                    renderLabelAndInput(hasOtherValue, () => {
                        const valuesWithoutOther = values.filter(value => allOptionKeys.indexOf(value) !== -1);

                        if (hasOtherValue) {
                            this.setSubmission(valuesWithoutOther);
                        } else {
                            if (radio) {
                                this.setSubmission([
                                    '',
                                ]);
                            } else {
                                this.setSubmission([
                                    ...valuesWithoutOther,
                                    '',
                                ]);
                            }

                            const input = document.getElementById(this.uniqueFieldId() + '_other');

                            if (input) {
                                input.focus();
                            }
                        }
                    }, this.isFillDisabled() || display, '___other', [
                        ' ',
                        app.translator.trans('kilowhat-formulaire.forum.choice.other-label'),
                        ' ',
                    ]),
                    display ? m('span', otherValue) : m('input.FormControl', {
                        type: 'text',
                        id: this.uniqueFieldId() + '_other',
                        placeholder: app.translator.trans('kilowhat-formulaire.forum.choice.other-placeholder'),
                        value: otherValue,
                        onchange: withAttr('value', otherOnChange),
                        onclick: () => {
                            // When focusing on the field by clicking, we want to immediately show it as checked
                            // like it does if you click on the "Other" label
                            if (!hasOtherValue) {
                                otherOnChange('');
                            }
                        },
                    }),
                ]);
            })() : null,
        ]);
    }

    abstract fill(): Children

    fillView(): Children {
        const title = this.getConfig('title');
        const description = this.getConfig('description');
        const validationErrors = this.validationErrorsForThisField();

        //TODO: no background error color for "items" type (that would show for any child error)
        return m(FormGroup, {
            validationErrors,
            label: m('label', {
                for: this.uniqueFieldId(),
            }, [
                title ? title : m('em', app.translator.trans(translationPrefix + 'missing-title')),
                this.getConfig('required', false) ? m('span.FormulaireRequired', ' *') : null,
            ]),
            description,
        }, this.fill());
    }

    display(): Children {
        const submission = this.getSubmission();

        let hasSubmission = !!submission;

        // We shouldn't be rendering array values here but this method is used as the default for Select
        // so we just need to handle the empty array as no answer
        if (Array.isArray(submission)) {
            hasSubmission = submission.length > 0;
        }

        return m('p', hasSubmission ? submission : m('em', app.translator.trans(translationPrefix + 'no-answer')));
    }

    displayView(): Children {
        const title = this.getConfig('title');

        return m('dl.Formulaire-field-display', [
            m('dt', [
                title ? title : m('em', app.translator.trans(translationPrefix + 'missing-title')),
            ]),
            m('dd', this.display()),
        ]);
    }

    optionsRule() {
        // We don't need to, and can't actually deep clone the object
        // Because the way Sortable works it will always work with the original onsort callback
        let options: OptionDefinition[] = this.getConfig('options', []);

        if (!Array.isArray(options)) {
            console.warn('Invalid options', options);
            options = [];
        }

        const showNewLine = !options.length || options[options.length - 1].title !== '';

        return m('.Form-group', m('table.FormulaireItemsTable', [
            this.expertMode ? m('thead', [
                m('th'),
                m('th', app.translator.trans('kilowhat-formulaire.forum.options-editor.columns.title')),
                m('th', app.translator.trans('kilowhat-formulaire.forum.options-editor.columns.key')),
                m('th'),
            ]) : null,
            m(Sortable, {
                onsort: (origin: number, destination: number) => {
                    options.splice(destination, 0, options.splice(origin, 1)[0]);
                    this.setConfig('options')(options);
                },
                containerTag: 'tbody',
                placeholderTag: 'tr',
            }, options.map((option, index) => m('tr', {
                key: 'item' + index, // Just so Sortable can work
            }, [
                m(SortableHandle, {
                    elementTag: 'td',
                    className: 'FormulaireItemsHandle',
                }),
                m('td', m('input.FormControl', {
                    type: 'text',
                    value: option.title,
                    onchange: withAttr('value', (value: string) => {
                        option.title = value;
                        this.setConfig('options')(options);
                    }),
                })),
                this.expertMode ? m('td', m('input.FormControl', {
                    type: 'text',
                    value: option.key,
                    onchange: withAttr('value', (value: string) => {
                        option.key = value;
                        this.setConfig('options')(options);
                    }),
                })) : null,
                m('td', Button.component({
                    onclick: () => {
                        options.splice(index, 1);
                        this.setConfig('options')(options);
                    },
                    className: 'Button Button--icon',
                    icon: 'fas fa-trash',
                    disabled: this.isFillDisabled(),
                    title: app.translator.trans('kilowhat-formulaire.forum.options-editor.remove'),
                }))
            ]))),
            showNewLine ? m('tbody', m('tr', [
                m('td'),
                m('td', m('input.FormControl', {
                    type: 'text',
                    value: '',
                    onchange: withAttr('value', (value: string) => {
                        options.push({
                            title: value,
                        });
                        this.setConfig('options')(options);
                    }),
                    placeholder: app.translator.trans('kilowhat-formulaire.forum.options-editor.new-placeholder'),
                })),
                m('td'),
            ])) : null,
        ]));
    }

    otherRule() {
        return m('.Form-group', Switch.component({
            state: this.getConfig('other', false),
            onchange: this.setConfig('other'),
            disabled: this.disabled,
        }, app.translator.trans('kilowhat-formulaire.forum.template-editor.other')));
    }

    minMaxRules(type = 'number') {
        return m('.Form-group', [
            m('.FormulaireInputWithLabel', {
                className: this.getConfig('min') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('input.FormControl', {
                    type,
                    step: type === 'number' ? 1 : undefined,
                    min: type === 'number' ? 0 : undefined,
                    value: this.getConfig('min'),
                    onchange: withAttr('value', (value: any) => {
                        if (type === 'number' && value) {
                            value = parseInt(value);
                        }

                        this.setConfig('min')(value);
                    }),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'min')),
            ]),
            m('.FormulaireInputWithLabel', {
                className: this.getConfig('max') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('input.FormControl', {
                    type,
                    step: type === 'number' ? 1 : undefined,
                    min: type === 'number' ? 0 : undefined,
                    value: this.getConfig('max'),
                    onchange: withAttr('value', (value: any) => {
                        if (type === 'number' && value) {
                            value = parseInt(value);
                        }

                        this.setConfig('max')(value);
                    }),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'max')),
            ]),
        ]);
    }

    isFillDisabled() {
        return this.disabled || this.getConfig('cannotFill');
    }

    validationErrorsForThisField(): string[] | undefined {
        const theKeyInValidationErrorTree = this.validationErrors[this.getConfig('key')];

        return theKeyInValidationErrorTree?.errors;
    }
}
